import { useState, useCallback } from 'react';

const useRailsAuth = (baseUrl = '') => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  const getCookie = useCallback((name) => {
    if (typeof document === 'undefined') return null;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }, []);

  const getUserSession = useCallback(async (email, password) => {
    const brandId = (typeof window !== 'undefined' && window.gon?.current_brand) || 'shipplay';
      
    try {
      const sessionResponse = await fetch(`${baseUrl}/api/v5/sessions?brand_id=${brandId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include',
        body: JSON.stringify({
          login_id: email,
          password: password,
          frontend_app: true,
        })
      });

      if (sessionResponse.ok) {
        const sessionData = await sessionResponse.json();
        const authToken = sessionData.auth_token || sessionData.token;
        return { ...sessionData, authToken };
      } else {
        console.warn('Could not retrieve auth token from session API, status:', sessionResponse.status);
        throw new Error('Failed to retrieve user session');
      }
    } catch (sessionError) {
      console.warn('Error calling session API:', sessionError);
      throw new Error('Failed to retrieve user session');
    }
  }, [baseUrl]);

  const checkExistingAuth = useCallback(() => {
    if (typeof window === 'undefined') return null;
    
    // Check for existing authentication via window.gon and window.user_data
    if (window.gon && window.user_data) {
      const gonData = window.gon;
      const userData = window.user_data;
      
      // Extract user data from both gon and user_data structures
      const existingUser = {
        id: userData.user_id || gonData.user_id,
        email: userData.user_email,
        firstName: userData.user_first_name,
        lastName: userData.user_last_name,
        full_name: `${userData.user_first_name || ''} ${userData.user_last_name || ''}`.trim(),
        phone_number: userData.phone || gonData.phone_number,
        role: userData.role || (gonData.is_admin ? 'admin' : gonData.is_pro ? 'pro' : 'user'),
        isPro: gonData.is_pro || gonData.is_travel_agent || gonData.is_camp_manager,
        admin: userData.is_admin || gonData.is_admin,
        pro: gonData.is_pro,
        mobile_verification_eligible: gonData.mobile_verification_eligible,
        mobile_verification_override: false,
        mobile_verified: gonData.mobile_verified,
        // Additional user_data fields
        active_shipments: userData.active_shipments,
        country_of_residence: userData.country_of_residence,
        orders_lifetime: userData.orders_lifetime,
        registered_device: userData.registered_device,
        shipment_stats: {
          lifetime_shipment_shipsticks: userData.lifetime_shipment_shipsticks,
          lifetime_shipment_shipplay: userData.lifetime_shipment_shipplay,
          lifetime_shipment_shipcamps: userData.lifetime_shipment_shipcamps,
          lifetime_shipment_shipskis: userData.lifetime_shipment_shipskis,
          lifetime_shipment_shipgo: userData.lifetime_shipment_shipgo,
          lifetime_shipment_luggagefree: userData.lifetime_shipment_luggagefree,
          total_golf_shipments: userData.total_golf_shipments,
          total_ski_shipments: userData.total_ski_shipments,
          total_snowboard_shipments: userData.total_snowboard_shipments,
          total_snowboot_shipments: userData.total_snowboot_shipments,
          total_sticks_luggage_shipments: userData.total_sticks_luggage_shipments,
          total_skis_luggage_shipments: userData.total_skis_luggage_shipments,
          total_away_luggage_shipments: userData.total_away_luggage_shipments,
          total_go_luggage_shipments: userData.total_go_luggage_shipments
        }
      };

      const existingAuth = {
        success: true,
        authToken: gonData.auth_token,
        redirectUrl: '/',
        user: existingUser,
        permissions: {
          admin: userData.is_admin || gonData.is_admin,
          pro: gonData.is_pro || gonData.is_travel_agent || gonData.is_camp_manager,
          carrierDropdown: userData.is_admin || gonData.is_admin
        },
        clubId: null,
        terms: userData.terms === 'true' || gonData.terms_accepted || gonData.accepted_terms,
        // Additional gon-specific data
        gonData: {
          env: gonData.env,
          current_brand: gonData.current_brand,
          partner_id: gonData.partner_id,
          subdomain: gonData.subdomain,
          active_products: gonData.active_products,
          countries: gonData.countries,
          theme: gonData.theme
        }
      };

      setUser(existingAuth.user);
      return existingAuth;
    }
    return null;
  }, []);

  const fetchUserDetails = useCallback(async (authToken) => {
    try {
      const userResponse = await fetch(`${baseUrl}/api/v5/users/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'x-user-token': authToken
        },
        credentials: 'include'
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();
        return {
          id: userData.id,
          email: userData.email,
          firstName: userData.first_name,
          lastName: userData.last_name,
          full_name: `${userData.first_name || ''} ${userData.last_name || ''}`.trim(),
          phone_number: userData.phone || '',
          role: userData.role || (userData.is_admin ? 'admin' : userData.is_pro ? 'pro' : 'user'),
          isPro: userData.is_pro || userData.is_travel_agent || userData.is_camp_manager,
          admin: userData.is_admin || false,
          pro: userData.is_pro || false,
          mobile_verification_eligible: userData.mobile_verification_eligible || false,
          mobile_verification_override: userData.mobile_verification_override || false,
          mobile_verified: userData.mobile_verified || false
        };
      } else {
        console.warn('Failed to fetch user details after login, status:', userResponse.status);
        return null;
      }
    } catch (error) {
      console.warn('Error fetching user details:', error);
      return null;
    }
  }, [baseUrl]);

  const login = useCallback(async (email, password) => {
    const useLegacyAuth = false; // Set to true to use Rails auth endpoint

    if (!email || !password) {
      throw new Error('Email and password are required');
    }

    setIsLoading(true);
    setError(null);

    try {
      let authData = {};

      // Check if user is already signed in via cookie
      const signedInCookie = getCookie('ss-signed-in');
      const isSignedIn = signedInCookie && signedInCookie.length > 0;

      if (!isSignedIn && useLegacyAuth) {
        // Sign in via Rails endpoint
        const loginData = {
          user: {
            email: email,
            password: password
          },
          from_static: true // This bypasses Rails CSRF protection
        };

        const response = await fetch(`${baseUrl}/users/sign_in`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'include',
          body: JSON.stringify(loginData)
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || data.errors?.[0] || 'Login failed');
        }

        // Extract the returned data from Rails controller
        authData = {
          success: data.success,
          formAuthenticityToken: data.akt,
          redirectUrl: data.redirect,
          user: data.user ? {
            id: data.user_id,
            role: data.user_role,
            isPro: data.pro_user,
            firstName: data.user.full_name?.split(' ')[0] || '',
            lastName: data.user.full_name?.split(' ').slice(1).join(' ') || '',
            email: data.user.email,
            full_name: data.user.full_name,
            phone_number: data.user.phone_number,
            admin: data.user.admin || false,
            pro: data.user.pro || false,
            mobile_verification_eligible: data.user.mobile_verification_eligible || false,
            mobile_verification_override: data.user.mobile_verification_override || false,
            mobile_verified: data.user.mobile_verified || false
          } : null,
          permissions: {
            admin: data.user?.admin || false,
            pro: data.user?.pro || false,
            carrierDropdown: data.carrier_dropdown || false
          },
          clubId: data.club_id || null,
          terms: data.terms || false
        };
      }

      // Get the user session to obtain the auth token
      const sessionData = await getUserSession(email, password);
      if (sessionData?.authToken) {
        authData.authToken = sessionData.authToken;
        const sessionUser = {
          id: sessionData.id,
          email: sessionData.email,
          firstName: sessionData.first_name,
          lastName: sessionData.last_name,
          full_name: `${sessionData.first_name || ''} ${sessionData.last_name || ''}`.trim(),
          phone_number: sessionData.phone_number,
          role: sessionData.admin_user ? 'admin' : sessionData.pro_user ? 'pro' : 'user',
          isPro: sessionData.pro_user || false,
          admin: sessionData.admin_user || false,
          pro: sessionData.pro_user || false,
          mobile_verification_eligible: sessionData.mobile_verification_eligible || false,
          mobile_verification_override: false, // Not provided in sessionData
          mobile_verified: sessionData.mobile_verified || false
        };
        // Merge with existing user data or use session data
        authData.user = sessionUser;  
      }

      // If we have an auth token but no user details, fetch them
      if (authData.authToken && !authData.user?.id) {
        const userDetails = await fetchUserDetails(authData.authToken);
        if (userDetails) {
          const details = {
            admin: userDetails.admin || false,
            email: userDetails.email || '',
            firstName: userDetails.first_name || '',
            full_name: userDetails.full_name || '',
            id: userDetails.id || '',
            isPro: userDetails.pro_user || false,
            lastName: userDetails.last_name || '',
            mobile_verification_eligible: userDetails.mobile_verification_eligible || false,
            mobile_verification_override: false, // Not provided in userDetails
            mobile_verified: userDetails.mobile_verified || false,
            phone_number: userDetails.phone_number || '',
            pro: userDetails.pro || false,
            role: userDetails.admin_user ? 'admin' : userDetails.pro_user ? 'pro' : 'user'
          };
          authData.user = details;
        }
      }

      setUser(authData.user);
      return authData;

    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [baseUrl, getCookie, getUserSession, fetchUserDetails]);

  const logout = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${baseUrl}/users/sign_out`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include'
      });

      setUser(null);
      return response.ok;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [baseUrl]);

  const checkAuthStatus = useCallback(async () => {
    try {
      const response = await fetch(`${baseUrl}/auth/status`, {
        headers: {
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.authenticated) {
          setUser(data.user);
          return data;
        }
      }
      return null;
    } catch (err) {
      console.warn('Auth status check failed:', err);
      return null;
    }
  }, [baseUrl]);

  return {
    login,
    logout,
    checkExistingAuth,
    checkAuthStatus,
    isLoading,
    error,
    user,
    isAuthenticated: !!user
  };
};

export default useRailsAuth;